// Phase 2.3 Lite — Event-side link panel.
// Surfaces the Guest invitation URL alongside the existing review-link controls
// inside Event Setup → Digital Invitation tab. This is the FIRST explicit
// Guest invitation copy/open affordance in the admin UI — keeps it clearly
// distinct from the private Client review link.
//
// IMPORTANT: When the event is NOT published yet, we render a "Draft" badge
// and surface a quiet helper line — we never imply the link is ready to share
// with real guests. A small "Open preview" affordance uses the existing
// preview_token (read-only, owner-only path) when available.

import React from 'react';
import LinkActionRow from '../links/LinkActionRow';
import { LINK_TYPES, toAbsoluteUrl } from '../../lib/utils/linkUtils';

export default function EventLinksPanel({ rsvpPageInfo }) {
  if (!rsvpPageInfo) return null;
  const slug = rsvpPageInfo.slug;
  const isPublished = !!rsvpPageInfo.is_published;
  const previewToken = rsvpPageInfo.preview_token;

  if (!slug) {
    // No invitation engine attached yet — render an inert helper card.
    return (
      <div
        className="p-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400"
        data-testid="event-links-panel-empty"
      >
        Set up the invitation first to get a guest link.
      </div>
    );
  }

  const guestUrl = `/invite/${slug}`;
  const previewUrl = !isPublished && previewToken
    ? `/invite/${slug}?preview=${previewToken}`
    : null;

  return (
    <div className="space-y-2" data-testid="event-links-panel">
      <LinkActionRow
        type={LINK_TYPES.EVENT_INVITATION}
        url={isPublished ? guestUrl : null}
        status={isPublished ? 'live' : 'draft'}
        helperText={
          isPublished
            ? undefined
            : 'Not published yet. Share with guests only after publishing.'
        }
        testidPrefix="event-guest-link"
      />
      {previewUrl && (
        <div
          className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-dashed border-gray-200 dark:border-gray-800 text-[11px] text-gray-500 dark:text-gray-400"
          data-testid="event-guest-preview-row"
        >
          <span>Want to QA before publishing? Use the preview link.</span>
          <a
            href={toAbsoluteUrl(previewUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="px-2 py-1 rounded-md font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
            data-testid="event-guest-preview-open-btn"
          >
            Open preview
          </a>
        </div>
      )}
    </div>
  );
}
